import { MultiStateButton } from "components/buttons/multiStateButton/MultiStateButton";
import { type SyntheticEvent, useMemo } from "react";
import styles from "./PackRequestButton.module.scss";
import { Button } from "semantic-ui-react";
import cn from "classnames";
import { Tooltip } from "components/common/tooltip";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

type Props = {
  isMultiple?: boolean;
  disabled?: boolean;
  hasPurchasePermission: boolean;
  hasPurchasePackAddon: boolean;
  triggerText: string;
  onTrialClick: (e: SyntheticEvent) => void;
  onRequestClick: (e: SyntheticEvent) => void;
  startTrialLabel: string;
  className?: string;
};

const noPermissionText = "You don't have permission to buy Packs. Please reach out to your administrator.";
const tooltipStyle = { width: "260px" };

export const PackRequestButton = ({
  isMultiple = false,
  disabled = false,
  triggerText,
  onTrialClick,
  onRequestClick,
  startTrialLabel,
  hasPurchasePermission,
  hasPurchasePackAddon,
  className = "",
}: Props) => {
  const isPaidPacksEnabled = !!useFeatureFlag(FeatureFlags.PaidPacks);

  const hasAddonButNoPermission = hasPurchasePackAddon && !hasPurchasePermission;

  const items = useMemo(
    () => [
      {
        text: triggerText,
        onClick: onRequestClick,
        divider: false,
        disabled: hasAddonButNoPermission,
        tooltipContent: noPermissionText,
        tooltipStyle,
        postfixIcon: isPaidPacksEnabled && hasAddonButNoPermission ? "exclamation-circle" : undefined,
        position: "left center",
      },
      {
        text: startTrialLabel,
        onClick: onTrialClick,
        divider: false,
      },
    ],
    [onRequestClick, onTrialClick, startTrialLabel, triggerText, hasAddonButNoPermission, isPaidPacksEnabled],
  );

  if (isMultiple) {
    return (
      <MultiStateButton
        className={cn(styles.button, className)}
        id="request-pack"
        data-testid="request-pack-buttons"
        trigger={triggerText}
        simple
        items={items}
        disabled={disabled}
        upward={false}
        menuDirection={"left"}
      />
    );
  }

  return (
    <Tooltip
      target={
        <Button
          id="request-pack-button"
          className={cn(styles.button, className)}
          data-testid="open-pack-request-modal-button"
          onClick={onRequestClick}
          disabled={disabled || (hasAddonButNoPermission && isPaidPacksEnabled)}
          primary
        >
          {triggerText}
        </Button>
      }
      styles={tooltipStyle}
      content={noPermissionText}
      position={"top center"}
      showAlways={isPaidPacksEnabled && hasAddonButNoPermission}
    />
  );
};
